import { useContext } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import {
  SkillsClientContext,
  SKILLS_CLIENT_STATES,
} from 'components/shared/Skills/SkillsClientProvider';
import { queryClient } from 'components/shared/ReactQuery/ReactQueryProvider';
import { hasNonNullValues } from 'utils/string_utils';

export function useGetSkillTemplatesForLessonlyContent({
  lessonlyContentId,
  lessonlyContentType,
  skip = false,
}) {
  const { client, status } = useContext(SkillsClientContext);
  const variables = {
    lessonlyContentId,
    lessonlyContentType,
  };
  return useQuery(
    [
      'get-skill-templates-for-lessonly-content',
      lessonlyContentId,
      lessonlyContentType,
    ],
    async () => {
      return client.request(
        gql`
          query GetSkillTemplatesForLessonlyContent(
            $lessonlyContentId: Int!
            $lessonlyContentType: String
          ) {
            skillTemplatesForLessonlyContent(
              lessonlyContentId: $lessonlyContentId
              lessonlyContentType: $lessonlyContentType
            ) {
              skillTemplates {
                id
                name
                description
              }
            }
          }
        `,
        variables,
      );
    },
    {
      enabled:
        !skip &&
        hasNonNullValues(variables) &&
        status === SKILLS_CLIENT_STATES.SUCCESS,
    },
  );
}

export function useSearchSkillTemplatesWithSearchTerm({
  searchTerm,
  limit,
  offset,
  hasNoCategory,
  mostRecent,
  excludedIds,
  skip = false,
}) {
  const { client, status } = useContext(SkillsClientContext);
  const variables = {
    searchTerm,
    limit,
    offset,
    hasNoCategory,
    mostRecent,
    excludedIds,
  };

  return useQuery(
    [
      'search-skill-templates',
      searchTerm,
      limit,
      offset,
      hasNoCategory,
      mostRecent,
      excludedIds,
    ],
    async () => {
      return client.request(
        gql`
          query getSkillTemplates(
            $searchTerm: String
            $hasNoCategory: Boolean
            $limit: Int!
            $offset: Int!
            $mostRecent: Boolean
            $excludedIds: [Int]
          ) {
            skillTemplates(
              searchTerm: $searchTerm
              hasNoCategory: $hasNoCategory
              limit: $limit
              offset: $offset
              mostRecent: $mostRecent
              excludedIds: $excludedIds
            ) {
              skillTemplates {
                name
                description
                id
                groupCount
                skillCategory {
                  id
                  name
                }
                roleTemplateCount
                createdAt
              }
              totalCount
            }
          }
        `,
        variables,
      );
    },
    {
      enabled: !skip && status === SKILLS_CLIENT_STATES.SUCCESS,
    },
  );
}

export function useSyncContentsAssociatedSkillTemplates({
  lessonlyContentId,
  lessonlyContentType,
}) {
  const { client } = useContext(SkillsClientContext);

  return useMutation(
    {
      mutationFn: variables =>
        client.request(
          gql`
            mutation SyncContentsAssociatedSkillTemplates(
              $lessonlyContentId: Int!
              $lessonlyContentType: String!
              $skillTemplateIds: [Int]!
            ) {
              syncContentsAssociatedSkillTemplates(
                lessonlyContentId: $lessonlyContentId
                lessonlyContentType: $lessonlyContentType
                skillTemplateIds: $skillTemplateIds
              )
            }
          `,
          variables,
        ),
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'get-skill-templates-for-lessonly-content',
          lessonlyContentId,
          lessonlyContentType,
        ]);
      },
    },
  );
}
